import { useState } from "react"

import { useForm } from "@tanstack/react-form"
import { toast } from "sonner"

import { FormInput } from "@/components/FormInput"
import { FormLabel } from "@/components/FormLabel"
import { Button } from "@/components/ui/Button"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog"
import { DropdownDatalist } from "@/components/DropdownDatalist"
import { Field } from "@/components/ui/Field"

import { ConfirmationDialog } from "../../components/ConfirmationDialog"
import { LoadingDialog } from "../../pricing/components/contact-partnership-dialog-components/LoadingDialog"
import { ErrorDialog } from "../../pricing/components/contact-partnership-dialog-components/ErrorDialog"

interface DeleteAccountDialogProps {
  isOpen: boolean
  deleteOptions: string[]
  errorMessage?: string | null
  deleteReason: string
  onClose: () => void
  onSubmit: (reason: string) => void
  onConfirmDelete: (deleteReason: string) => Promise<void>
}

export function DeleteAccountDialog({
  isOpen,
  deleteOptions,
  errorMessage = null,
  deleteReason,
  onClose,
  onSubmit,
  onConfirmDelete,
}: DeleteAccountDialogProps) {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const [showLoadingDialog, setShowLoadingDialog] = useState(false)
  const [showErrorDialog, setShowErrorDialog] = useState(false)

  const form = useForm({
    defaultValues: {
      deleteReason: "",
    },
    onSubmit: ({ value }) => {
      const reason = value.deleteReason?.trim()
      if (!reason) {
        return
      }

      onSubmit(reason)
      setShowConfirmDialog(true)
    },
  })

  async function handleConfirmDelete(): Promise<void> {
    setShowConfirmDialog(false)
    setShowLoadingDialog(true)

    try {
      await onConfirmDelete(deleteReason)

      setShowLoadingDialog(false)

      toast.success("Deleted Account", {
        position: "bottom-right",
      })

      onClose()
    } catch (error) {
      setShowLoadingDialog(false)
      setShowErrorDialog(true)
    }
  }

  function handleCancelConfirm(): void {
    setShowConfirmDialog(false)
  }

  function handleCloseError(): void {
    setShowErrorDialog(false)
  }

  return (
    <>
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="tw-max-w-3xl">
          <DialogHeader>
            <DialogTitle>Confirm Account Delete</DialogTitle>
          </DialogHeader>

          <form
            onSubmit={(event) => {
              event.preventDefault()
              event.stopPropagation()
              form.handleSubmit()
            }}
          >
            <div className="tw-space-y-4 tw-py-4">
              <div className="tw-space-y-2">
                <p className="tw-text-sm tw-text-red-600">
                  Deleting your account cannot be reversed. All of your data
                  will be deleted and cannot be recovered.
                </p>
                <p className="tw-text-sm tw-text-gray-600">
                  Are you sure you want to continue?
                </p>
              </div>

              <form.Field
                name="deleteReason"
                children={(field) => (
                  <Field>
                    <FormLabel
                      field={field}
                      label="Please tell us why you're deleting your account:"
                    />
                    <div className="tw-py-2">
                      <FormInput
                        field={field}
                        list="delete-reason-options"
                        autoComplete="off"
                        className="tw-w-full tw-pl-3 tw-pr-10 tw-py-2"
                      />
                      <DropdownDatalist
                        id="delete-reason-options"
                        options={deleteOptions}
                      />
                    </div>
                  </Field>
                )}
              />
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={onClose}>
                CANCEL
              </Button>
              <form.Subscribe selector={(state) => [state.values.deleteReason]}>
                {([deleteReason]) => {
                  const isDisabled = !deleteReason?.trim()

                  return (
                    <Button
                      type="submit"
                      variant="destructive"
                      disabled={isDisabled}
                      className="tw-min-w-[100px]"
                    >
                      DELETE ACCOUNT
                    </Button>
                  )
                }}
              </form.Subscribe>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmationDialog
        open={showConfirmDialog}
        title="Are you sure you want to delete your account?"
        message="You cannot recover anything after it is deleted."
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelConfirm}
      />

      {showLoadingDialog && (
        <LoadingDialog
          message="Deleting your account..."
          onClose={() => setShowLoadingDialog(false)}
        />
      )}

      {showErrorDialog && (
        <ErrorDialog
          title="Error"
          message={errorMessage ?? "There was a problem deleting your account."}
          onClose={handleCloseError}
        />
      )}
    </>
  )
}
